import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { loadBuildingsForSelector } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  getBuildingSelectProps,
  needsBuildingSelection,
  shouldShowBuildingSelector,
  selectBuilding,
} from '../../../utils/buildingSelection';
import { ActivityVoucherActionLog, incomeExpenseApi } from '../../../api/incomeExpenseApi';
import { BulkListPagination } from '../../../components/bulk-fee/BulkListPagination';
import { HISTORY_PAGE_SIZE } from '../../../api/paginatedHistory';
import { useLang } from '../../../contexts/LangContext';

const formatActionLabel = (action: ActivityVoucherActionLog['action']) =>
  action === 'UPDATE' ? 'Sửa' : 'Huỷ';

const actionBadgeClass = (action: ActivityVoucherActionLog['action']) =>
  action === 'UPDATE' ? 'bg-cyan-100 text-cyan-700' : 'bg-red-100 text-red-700';

interface ActivityVoucherActionHistoryTabProps {
  kind: 'revenue' | 'expenditure';
  refreshToken?: number;
  onBuildingIdChange?: (buildingId: string) => void;
}

export const ActivityVoucherActionHistoryTab: React.FC<ActivityVoucherActionHistoryTabProps> = ({
  kind,
  refreshToken = 0,
  onBuildingIdChange,
}) => {
  const { user } = useAuth();
  const { t } = useLang();
  const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
  const [buildingId, setBuildingId] = useState('');
  const [logs, setLogs] = useState<ActivityVoucherActionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const voucherLabel = kind === 'revenue' ? 'phiếu thu' : 'phiếu chi';

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    });
  }, [user]);

  useEffect(() => {
    if (buildingId) onBuildingIdChange?.(buildingId);
  }, [buildingId, onBuildingIdChange]);

  const loadLogs = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const result =
        kind === 'revenue'
          ? await incomeExpenseApi.listRevenueVoucherActionLogs(buildingId, {
              page,
              limit: HISTORY_PAGE_SIZE,
            })
          : await incomeExpenseApi.listExpenditureVoucherActionLogs(buildingId, {
              page,
              limit: HISTORY_PAGE_SIZE,
            });
      setLogs(result.data);
      setTotal(result.meta.total);
      setTotalPages(result.meta.totalPages);
    } catch {
      toast.error('Không thể tải lịch sử thao tác phiếu');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId, kind, page]);

  useEffect(() => {
    setPage(1);
  }, [buildingId, kind]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs, refreshToken]);

  return (
    <div className="space-y-4">
      {shouldShowBuildingSelector(buildings) && (
        <select
          value={buildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
          {...getBuildingSelectProps(buildings)}
          className="rounded-lg border px-3 py-2 text-sm"
        >
          {needsBuildingSelection(buildings) && <option value="">-- Chọn tòa nhà --</option>}
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      )}

      <p className="text-sm text-gray-600">
        Lịch sử chỉnh sửa và huỷ {voucherLabel} tạo từ Hoạt động {kind === 'revenue' ? 'thu' : 'chi'}.
      </p>

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Thời gian</th>
              <th className="px-4 py-3 text-left">Thao tác</th>
              <th className="px-4 py-3 text-left">Số bút toán</th>
              <th className="px-4 py-3 text-left">Chi tiết</th>
              <th className="px-4 py-3 text-left">Người thực hiện</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">Đang tải...</td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                  Chưa có lịch sử sửa/huỷ {voucherLabel}
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="border-t">
                  <td className="px-4 py-3 whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString('vi-VN')}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${actionBadgeClass(log.action)}`}
                    >
                      {formatActionLabel(log.action)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{log.journalNumber || '—'}</td>
                  <td className="px-4 py-3 min-w-[240px] max-w-xl whitespace-normal break-words">
                    {log.notes || '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {log.creator?.name || log.creator?.email || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="p-4">
          <BulkListPagination
            page={page}
            totalPages={totalPages}
            total={total}
            onPageChange={setPage}
            t={t}
          />
        </div>
      </div>
    </div>
  );
};

export default ActivityVoucherActionHistoryTab;
